import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, LogOut, Receipt, QrCode, ChevronDown, Settings, HelpCircle
} from 'lucide-react';
import Abbonamenti from '../../components/utente/Abbonamenti';
import Pagamenti from '../../components/utente/Pagamenti';
import Profilo from '../../components/utente/Profilo';
import PullToRefreshIndicator from '../../components/utente/PullToRefresh';
import usePullToRefresh from '../../hooks/usePullToRefresh';
import authStore from '../../store/authStore';
import api from '../../services/api';
import './Dashboard.css';

const DashboardUtente = () => {
  const navigate = useNavigate();
  const { utente, logout } = authStore();
  const [activeTab, setActiveTab] = useState('abbonamenti');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [titolare, setTitolare] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const dropdownRef = useRef(null);
  const contentRef = useRef(null);

  const handleRefresh = async () => {
    await caricaTitolare();
    setRefreshKey(prev => prev + 1);
  };

  const { isRefreshing, pullDistance } = usePullToRefresh(handleRefresh, {
    threshold: 80,
    resistance: 2.5,
    enabled: true,
    hapticFeedback: true
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const caricaTitolare = async () => {
    try {
      const response = await api.get('/anagrafiche/titolari/me/');
      setTitolare(response.data);
    } catch (error) {
      console.error('Errore caricamento titolare:', error);
    }
  };

  useEffect(() => { caricaTitolare(); }, []);

  // Cliente nuovo con dati incompleti: fino a quando non li compila non
  // ha senso mostrargli abbonamenti e pagamenti, perché il tesserino non
  // è comunque utilizzabile. Si tiene aperto il solo profilo.
  const anagraficaDaCompletare = titolare !== null && titolare.anagrafica_completa === false;

  useEffect(() => {
    if (anagraficaDaCompletare) setActiveTab('profilo');
  }, [anagraficaDaCompletare]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const tabsComplete = [
    { id: 'abbonamenti', label: 'Abbonamenti', icon: QrCode },
    { id: 'pagamenti', label: 'Pagamenti', icon: Receipt },
    { id: 'profilo', label: 'Profilo', icon: User },
  ];
  const tabs = anagraficaDaCompletare
    ? tabsComplete.filter((t) => t.id === 'profilo')
    : tabsComplete;

  const handleTabChange = (tabId) => {
    if (window.navigator?.vibrate) window.navigator.vibrate(10);
    setActiveTab(tabId);
    setDropdownOpen(false);
    if (contentRef.current) contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = () => {
    if (anagraficaDaCompletare) {
      return <Profilo key={`pr-${refreshKey}`} onSalvato={caricaTitolare} />;
    }
    switch (activeTab) {
      case 'abbonamenti': return <Abbonamenti key={`a-${refreshKey}`} />;
      case 'pagamenti': return <Pagamenti key={`p-${refreshKey}`} />;
      case 'profilo': return <Profilo key={`pr-${refreshKey}`} onSalvato={caricaTitolare} />;
      default: return <Abbonamenti key={`a-${refreshKey}`} />;
    }
  };

  return (
    <div className="du-root">
      {/* Pull to Refresh */}
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} threshold={80} />

      {/* HEADER */}
      <header className="du-header">
        <div className="du-header-inner">
          <img src="/logo.png" alt="Casella" className="du-logo" />

          <div className="du-user-menu" ref={dropdownRef}>
            <button className="du-user-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
              <div className="du-avatar">
                {titolare?.foto ? <img src={titolare.foto} alt="" /> : <User size={18} />}
              </div>
              <div className="du-user-text">
                <span className="du-user-name">
                  {titolare?.nome && titolare?.cognome
                    ? `${titolare.nome} ${titolare.cognome}`
                    : utente?.first_name || utente?.username}
                </span>
                <span className="du-user-role">Utente</span>
              </div>
              <ChevronDown size={18} className={`du-chevron ${dropdownOpen ? 'open' : ''}`} />
            </button>

            {dropdownOpen && (
              <div className="du-dropdown">
                <div className="du-dd-profile">
                  <div className="du-dd-avatar">
                    {titolare?.foto ? <img src={titolare.foto} alt="" /> : <User size={24} />}
                  </div>
                  <div className="du-dd-info">
                    <p className="du-dd-name">
                      {titolare?.nome && titolare?.cognome
                        ? `${titolare.nome} ${titolare.cognome}`
                        : utente?.username}
                    </p>
                    <p className="du-dd-email">{titolare?.email || utente?.email}</p>
                  </div>
                </div>

                <div className="du-dd-items">
                  <button className="du-dd-item" onClick={() => { setActiveTab('profilo'); setDropdownOpen(false); }}>
                    <User size={16} /><span>Il Mio Profilo</span>
                  </button>
                  {!anagraficaDaCompletare && (
                  <button className="du-dd-item" onClick={() => { setActiveTab('abbonamenti'); setDropdownOpen(false); }}>
                    <QrCode size={16} /><span>I Miei Abbonamenti</span>
                  </button>
                  )}
                  {!anagraficaDaCompletare && (
                  <button className="du-dd-item" onClick={() => { setActiveTab('pagamenti'); setDropdownOpen(false); }}>
                    <Receipt size={16} /><span>Storico Pagamenti</span>
                  </button>
                  )}

                  <div className="du-dd-sep"></div>

                  <button className="du-dd-item" onClick={() => alert('Funzionalità in arrivo')}>
                    <Settings size={16} /><span>Impostazioni</span>
                  </button>
                  <button className="du-dd-item" onClick={() => { window.location.href = 'mailto:info@casellanoleggiobus.it?subject=Richiesta Supporto'; }}>
                    <HelpCircle size={16} /><span>Supporto</span>
                  </button>

                  <div className="du-dd-sep"></div>

                  <button className="du-dd-item logout" onClick={handleLogout}>
                    <LogOut size={16} /><span>Esci</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <main className="du-main" ref={contentRef}>
        {anagraficaDaCompletare && (
          <div className="du-benvenuto">
            <h2>Benvenuto! Completa la registrazione</h2>
            <p>
              Per attivare l'abbonamento servono ancora questi dati:
              {' '}<b>{(titolare?.dati_mancanti || []).join(', ').toLowerCase()}</b>.
            </p>
            <p className="du-benvenuto-nota">
              Appena li salvi potrai vedere abbonamento e pagamenti, e il
              tesserino sarà valido a bordo.
            </p>
          </div>
        )}
        <div className="du-content-fade" key={activeTab}>
          {renderContent()}
        </div>
      </main>

      {/* BOTTOM NAV — con una sola sezione non serve */}
      <nav className="du-bottom-nav" style={tabs.length < 2 ? { display: 'none' } : undefined}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`du-nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <Icon size={22} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default DashboardUtente;
